import { CartographySupplier, ProductRaw, SectorStat, SupplierRaw } from "./types";

export const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5002";

export const ALGERIA_CENTER: [number, number] = [28.0339, 1.6596];

export const WILAYA_COORDINATES: Record<string, [number, number]> = {
  Adrar: [27.8743, -0.2939],
  Chlef: [36.1647, 1.3317],
  Laghouat: [33.8, 2.8651],
  "Oum El Bouaghi": [35.8754, 7.1135],
  Batna: [35.5559, 6.1741],
  Bejaia: [36.7515, 5.0557],
  Biskra: [34.8481, 5.7245],
  Bechar: [31.6167, -2.2167],
  Blida: [36.4702, 2.8289],
  Bouira: [36.3749, 3.902],
  Tamanrasset: [22.785, 5.5228],
  Tebessa: [35.4042, 8.1242],
  Tlemcen: [34.8783, -1.315],
  Tiaret: [35.371, 1.3169],
  "Tizi Ouzou": [36.7169, 4.0497],
  Alger: [36.7538, 3.0588],
  Djelfa: [34.6667, 3.25],
  Jijel: [36.821, 5.7667],
  Setif: [36.1911, 5.4137],
  Saida: [34.8415, 0.1456],
  Skikda: [36.8796, 6.9063],
  "Sidi Bel Abbes": [35.1917, -0.6417],
  Annaba: [36.9, 7.7667],
  Guelma: [36.4621, 7.4261],
  Constantine: [36.365, 6.6147],
  Medea: [36.2642, 2.7539],
  Mostaganem: [35.9333, 0.0833],
  Msila: [35.7058, 4.5419],
  Mascara: [35.3972, 0.1408],
  Ouargla: [31.9522, 5.3333],
  Oran: [35.6971, -0.6308],
  "El Bayadh": [33.6833, 1.0167],
  Illizi: [26.4833, 8.4667],
  "Bordj Bou Arreridj": [36.0732, 4.7611],
  Boumerdes: [36.7664, 3.4772],
  "El Tarf": [36.7672, 8.3138],
  Tindouf: [27.6742, -8.1478],
  Tissemsilt: [35.6072, 1.8108],
  "El Oued": [33.3561, 6.8632],
  Khenchela: [35.4358, 7.1433],
  "Souk Ahras": [36.2864, 7.9511],
  Tipaza: [36.5897, 2.4475],
  Mila: [36.4503, 6.2644],
  "Ain Defla": [36.264, 1.9679],
  Naama: [33.2667, -0.3167],
  "Ain Temouchent": [35.2975, -1.1404],
  Ghardaia: [32.4891, 3.6738],
  Relizane: [35.7373, 0.5559],
};

export const SECTOR_COLORS = [
  "#facc15",
  "#22c55e",
  "#0ea5e9",
  "#a855f7",
  "#f97316",
  "#ef4444",
  "#14b8a6",
  "#64748b",
];

export function normalizeText(value?: string | null): string {
  return String(value || "").trim();
}

export function displayValue(value: any, fallback = "Non renseigné") {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
}

export function toNumber(value: any): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function getSupplierLatLng(supplier: SupplierRaw): [number, number] {
  const lat = toNumber(supplier.geo_lat);
  const lng = toNumber(supplier.geo_lng);
  if (lat !== null && lng !== null) return [lat, lng];
  return WILAYA_COORDINATES[normalizeText(supplier.wilaya)] || ALGERIA_CENTER;
}

export function getProductCapacityGap(product: ProductRaw): number | null {
  const capacity = toNumber(product.annual_capacity);
  const requested = toNumber(product.requested_capacity);
  if (capacity === null || requested === null) return null;
  return capacity - requested;
}

export function getCapacityStatus(product: ProductRaw): string {
  const gap = getProductCapacityGap(product);
  if (gap === null) return "Non renseigné";
  return gap < 0 ? "Non capacitaire" : "Capacitaire";
}

export function getBadgeClass(status?: string | null): string {
  const value = normalizeText(status).toLowerCase();
  if (!value || value === "non renseigné") return "aida-badge-muted";
  if (value.includes("non capacitaire") || value.includes("bloquant") || value.includes("refus") || value.includes("manquant")) return "aida-badge-danger";
  if (value.includes("potentiel") || value.includes("cours") || value.includes("attente") || value.includes("partiel")) return "aida-badge-warning";
  if (value.includes("capacitaire") || value.includes("valid") || value.includes("conforme") || value.includes("export-ready") || value.includes("homolog")) return "aida-badge-success";
  return "aida-badge-muted";
}

export function getSectorColor(sector: string): string {
  let hash = 0;
  for (let i = 0; i < sector.length; i += 1) hash = sector.charCodeAt(i) + ((hash << 5) - hash);
  return SECTOR_COLORS[Math.abs(hash) % SECTOR_COLORS.length];
}

export function enrichSupplier(supplier: SupplierRaw, products: ProductRaw[]): CartographySupplier {
  const [lat, lng] = getSupplierLatLng(supplier);
  const totalCapacity = products.reduce((sum, p) => sum + Number(p.annual_capacity || 0), 0);
  const requestedCapacity = products.reduce((sum, p) => sum + Number(p.requested_capacity || 0), 0);
  const coverageRate = requestedCapacity > 0 ? Math.round((totalCapacity / requestedCapacity) * 100) : null;
  const nonCapacitaryProducts = products.filter((p) => getCapacityStatus(p) === "Non capacitaire").length;
  const exportableProducts = products.filter((p) => {
    const s = normalizeText(p.export_status).toLowerCase();
    return s.includes("export") || s.includes("potentiel");
  }).length;
  const homologatedProducts = products.filter((p) => {
    const s = normalizeText(p.homologation_status).toLowerCase();
    return s.includes("valid") || s.includes("homolog");
  }).length;
  const substitutionProducts = products.filter((p) => Number(p.import_substitution || 0) === 1).length;
  const mainSector = normalizeText(supplier.industrial_sector) || normalizeText(products[0]?.category) || "Non renseigné";
  return { ...supplier, products, lat, lng, mainSector, totalCapacity, requestedCapacity, coverageRate, nonCapacitaryProducts, exportableProducts, homologatedProducts, substitutionProducts };
}

export function buildSectorStats(suppliers: CartographySupplier[]): SectorStat[] {
  const map = new Map<string, SectorStat>();
  suppliers.forEach((supplier) => {
    const sector = supplier.mainSector || "Non renseigné";
    const current = map.get(sector) || { sector, suppliers: 0, products: 0, capacity: 0, requested: 0, coverageRate: null, exportable: 0, substitution: 0, alerts: 0 };
    current.suppliers += 1;
    current.products += supplier.products.length;
    current.capacity += supplier.totalCapacity;
    current.requested += supplier.requestedCapacity;
    current.exportable += supplier.exportableProducts;
    current.substitution += supplier.substitutionProducts;
    current.alerts += supplier.nonCapacitaryProducts;
    current.coverageRate = current.requested > 0 ? Math.round((current.capacity / current.requested) * 100) : null;
    map.set(sector, current);
  });
  return Array.from(map.values()).sort((a, b) => b.suppliers - a.suppliers);
}

export const ALGERIA_BOUNDARY: [number, number][] = [
  [37.05, -2.25],
  [36.90, 2.40],
  [37.05, 8.60],
  [35.80, 8.35],
  [34.75, 8.20],
  [32.75, 9.10],
  [30.20, 9.50],
  [26.80, 8.80],
  [23.60, 11.95],
  [19.10, 9.55],
  [18.95, 3.00],
  [20.80, -1.20],
  [25.00, -6.00],
  [27.70, -8.70],
  [31.60, -2.20],
  [34.90, -1.80],
  [37.05, -2.25],
];

export function formatNumber(value: number | string | null | undefined): string {
  const n = toNumber(value);
  if (n === null) return "—";
  return new Intl.NumberFormat("fr-FR").format(n);
}
